import { useI18n } from '@/libraries/contexts/i18n-context';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import { useFlightSelectorFormatTime } from '../../flight-selector-helpers/use-flight-selector-format-time';
import styles from './flight-selector-slide-section.module.scss';

interface FlightSelectorSlideSectionProps
  extends Content.Properties.FlightDetails {
  /** The text to show when flight times aren't available */
  noTimeText: string;
}

const FlightSelectorSlideSection = ({
  airlineCompanyLogoUrl,
  airlineCompanyName,
  arrivalCode,
  arrivalDate,
  arrivalName,
  charterType,
  departureCode,
  departureDate,
  departureName,
  isDirect,
  noTimeText,
  showTravelTime,
  stops,
  travelDays = 0,
}: FlightSelectorSlideSectionProps) => {
  const { t } = useI18n();
  const {
    arrivalHour,
    date,
    departureHour,
    differenceHours,
    differenceMinutes,
    month,
  } = useFlightSelectorFormatTime({
    arrivalDate,
    departureDate,
  });

  const hours = differenceHours.toFixed(0);
  const minutes = differenceMinutes.toFixed(0).padStart(2, '0');
  const duration = `${t('items.common.words.hoursShort', { hours })} ${t('items.common.words.minutesShort', { minutes })}`;

  const formattedDate = [departureHour, arrivalHour]
    .filter(Boolean)
    .join(' - ')
    .toLowerCase();

  const flightTypeLabel =
    charterType === 'scheduled'
      ? t('items.modules.flightSelector.flight.scheduledFlight')
      : charterType === 'charter'
        ? t('items.modules.flightSelector.flight.charterFlight')
        : null;

  const routeLabel = isDirect
    ? t('items.modules.flightSelector.flight.direct')
    : t('items.modules.flightSelector.flight.stops', { stops });

  const flightType = flightTypeLabel
    ? isDirect
      ? `${routeLabel} ${flightTypeLabel}`
      : `${routeLabel} - ${flightTypeLabel}`
    : routeLabel;

  return (
    <div className={styles.component}>
      <div className={styles.headline}>
        <Typography tagStyle="labelSmall">
          {`${flightType} ${charterType}/ ${date}. ${month}`}
        </Typography>

        {showTravelTime && (
          <Typography tagStyle="labelSmall">{duration}</Typography>
        )}
      </div>

      <div className={styles.flightInfo}>
        {airlineCompanyLogoUrl && (
          <Image
            alt={airlineCompanyName}
            className={styles.logo}
            height={32}
            src={airlineCompanyLogoUrl}
            unoptimized
            width={32}
          />
        )}

        <div className={styles.infoWrapper}>
          <Typography tagStyle="bodySmall">{airlineCompanyName}</Typography>

          <Typography className={styles.date} tagStyle="bodyMediumBold">
            {formattedDate}

            {travelDays > 0 && (
              <Typography
                className={styles.differenceDays}
                tagStyle="labelSmall"
              >
                {`+${travelDays}`}
              </Typography>
            )}
          </Typography>

          <div className={styles.infoWrapper}>
            <Typography tagStyle="bodySmall">
              {`${departureName} (${departureCode})`}

              <Icon className={styles.arrow} name="ArrowRight" size={8} />

              {`${arrivalName} (${arrivalCode})`}
            </Typography>
          </div>
        </div>
      </div>

      {!showTravelTime && noTimeText && (
        <Typography className={styles.flightDetails} tagStyle="bodySmall">
          <Icon name="Info" />

          {noTimeText}
        </Typography>
      )}
    </div>
  );
};

FlightSelectorSlideSection.displayName = 'FlightSelectorSlideSection';

export default FlightSelectorSlideSection;
